import React from "react";
import { graphql, useFragment } from "react-relay";

import { faLanguage } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";

import RegenerateForLanguageModal from "~/meetingRecorder/RegenerateForLanguageModal";
import {
	MenuItemToBeDisabledForExternalWorkspaces,
	MenuItemTooltip,
	useGetPropsForExternalWorkspaces,
} from "~/meetings/MeetingHeader/MoreActionsMenu/MenuItemTooltip";
import { useModals } from "~/ui/modals";

import type { RecapLanguage_note$key } from "~/__generated__/RecapLanguage_note.graphql";

const noteFragment = graphql`
	fragment RecapLanguage_note on Note {
		id
		viewerCanEdit
		isLocked
		stream {
			isExternal
		}
		meetingRecordings {
			id
			viewerCanDelete
			...RecapHistory_meetingRecording
			...utils_useLongestMeetingRecording_meetingRecordings
			...RegenerateForLanguageModalFragment_meetingRecording
		}
		...MenuItemTooltip_useGetPropsForExternalWorkspaces_note
	}
`;

export default function RecapLanguageMenuItem({
	note: noteKey,
	selectedRecordingId,
}: {
	note: RecapLanguage_note$key;
	selectedRecordingId: string;
}) {
	const modals = useModals();
	const note = useFragment(noteFragment, noteKey);

	const selectedRecording = note.meetingRecordings?.find(recording => recording.id === selectedRecordingId);

	const onSelectRecapLanguage = React.useCallback(() => {
		if (!selectedRecording) return;
		modals.open(({ close }) => (
			<RegenerateForLanguageModal meetingRecording={selectedRecording} close={close} noteId={note.id} />
		));
	}, [modals, note.id, selectedRecording]);

	const tooltipPropsForExternalWorkspaces = useGetPropsForExternalWorkspaces(note);

	return (
		<MenuItemToBeDisabledForExternalWorkspaces
			disabled={note.isLocked || !note.viewerCanEdit}
			icon={faLanguage}
			onSelect={onSelectRecapLanguage}
			{...tooltipPropsForExternalWorkspaces}
		>
			<MenuItemTooltip
				disabled={!(note.isLocked || !note.viewerCanEdit)}
				content={
					note.isLocked
						? "Cannot regenerate recap for locked notes."
						: !note.viewerCanEdit
							? "You have limited access to this meeting"
							: null
				}
			>
				<div>Recap language…</div>
			</MenuItemTooltip>
		</MenuItemToBeDisabledForExternalWorkspaces>
	);
}
